import { useEffect, useState } from 'react';

type SortOrder = 'asc' | 'desc';

const sortData = <T extends object>(
  data: T[],
  sortField: string,
  sortOrder: SortOrder,
): T[] => {
  return [...data].sort((a, b) => {
    const [field, subField] = sortField.split('.');

    const getSortValue = (item: T) => {
      const value = Object.entries(item).find(([key]) => key === field)?.[1];
      if (!subField || value === null || !(value instanceof Object)) return value;
      return Object.entries(value).find(([key]) => key === subField)?.[1];
    };
    const aValue = getSortValue(a);
    const bValue = getSortValue(b);

    if (aValue === null && bValue === null) return 0;
    if (aValue === null) return sortOrder === 'asc' ? 1 : -1;
    if (bValue === null) return sortOrder === 'asc' ? -1 : 1;

    return (
      (aValue?.toString() ?? '').localeCompare(bValue?.toString() ?? '', 'en', {
        numeric: true,
      }) * (sortOrder === 'asc' ? 1 : -1)
    );
  });
};

const useSortableData = <T extends object>(
  initialData: T[],
  defaultSortField: string,
  defaultSortOrder: SortOrder = 'asc',
) => {
  const [data, setData] = useState<T[]>(initialData);
  const [sortField, setSortField] = useState<string>(defaultSortField);
  const [sortOrder, setSortOrder] = useState<SortOrder>(defaultSortOrder);

  useEffect(() => {
    setData(initialData);
  }, [initialData]);

  useEffect(() => {
    if (sortField) {
      const sortedData = sortData(initialData, sortField, sortOrder);
      setData(sortedData);
    }
  }, [initialData, sortField, sortOrder]);

  const handleSorting = (field: string) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(order);
    const sortedData = sortData(data, field, order);
    setData(sortedData);
  };

  return { data, handleSorting, sortField, sortOrder };
};

export default useSortableData;
